import { TranslationUtils } from '../../lang/i18n';
import {Component, h, Prop, Watch} from '@stencil/core';


/**
 * Interface for installment data
 */
export interface Installment {
    amount: string;
    isCompleted?: boolean;
    progressType?: 'empty' | 'small' | 'medium' | 'full';
}

@Component({
    tag: 'amwal-installments-timeline',
    styleUrl: 'amwal-installments-timeline.css',
    shadow: false,
})
export class AmwalInstallmentsTimeline {

    /**
     * The amount for both initial payment and total to be paid in installments
     */
    @Prop() amount: number = 0;

    /**
     * Number of installments
     */
    @Prop() installmentsCount: number = 6;

    /**
     * The installment payments (optional - will be calculated if not provided)
     */
    @Prop() installmentData: string = '[]';

    /**
     * Enable or disable installments feature
     */
    @Prop() enableInstallments: boolean = false;

    /**
     * Maximum number of installments to display (for UI clarity)
     */
    @Prop() maxVisibleInstallments: number = 3;

    /**
     * the language of the plugin. supported languages are Arabic ("ar") and English ("en")
     */
    @Prop() locale?: string = "en";

    /**
     * Enable or disable border around the timeline
     */
    @Prop() border: boolean = false;

    /**
     * Enable debug mode for additional console logs
     * @default false
     */
    @Prop() debug: boolean = false;

    /**
     * Translation function to handle localization
     * @private
     */
    private t: (val: string) => string = (val) => val; // Initialize with fallback

    /**
     * Lifecycle method that is called once when the component is first created
     */
    async componentWillLoad() {
        // Initialize the translation function when component loads
        try {
            this.t = await TranslationUtils.useTranslation(this.locale);
        } catch (e) {
            console.error('Failed to initialize translation:', e);
            // Keep the fallback function defined above
        }
    }

    /**
     * Lifecycle method to update translations when locale changes
     * @param newValue
     */
    @Watch('locale')
    async updateLocale(newValue: string) {
        this.locale = newValue;
        try {
            this.t = await TranslationUtils.useTranslation(this.locale);
        } catch (e) {
            console.error('Failed to update translation:', e);
            // Fallback to using the input string as-is
            this.t = (val) => val;
        }
    }

    /**
     * Get installment data as parsed object or generate it dynamically
     */
    private get installments(): Installment[] {
        try {
            // First try to use the provided installmentData if available
            const providedData = JSON.parse(this.installmentData);
            if (providedData.length > 0) {
                return providedData;
            }

            // If no valid data is provided, calculate it dynamically
            return this.generateInstallments();
        } catch (e) {
            console.error('Invalid installment data format', e);
            // Generate installments dynamically if parsing fails
            return this.generateInstallments();
        }
    }

    /**
     * Generate installment data based on amount and installmentsCount
     */
    private generateInstallments(): Installment[] {
        const installments: Installment[] = [];
        if (this.amount <= 0 || this.installmentsCount <= 0) {
            return installments;
        }

        // Calculate the base installment amount (rounded to 2 decimal places)
        const baseAmount = +(this.amount / this.installmentsCount).toFixed(2);

        // Calculate the remainder to distribute
        const remainder = +(this.amount - (baseAmount * this.installmentsCount)).toFixed(2);

        // Create installment objects
        for (let i = 0; i < this.installmentsCount; i++) {
            // Add remainder to the last installment if needed
            const amount = i === this.installmentsCount - 1 && remainder !== 0
                ? (baseAmount + remainder).toFixed(1)
                : baseAmount.toFixed(1);

            // Assign progressive status based on position
            let progressType: 'empty' | 'small' | 'medium' | 'full' = 'empty';
            if (i === 0) {
                progressType = 'small';
            } else if (i === 1 || (i > 1 && i < this.installmentsCount - 1)) {
                progressType = 'medium';
            } else if (i === this.installmentsCount - 1) {
                progressType = 'full';
            }
            installments.push({
                amount: `${amount}`,
                progressType
            });
        }

        return installments;
    }

    /**
     * Format amount to display with SAR symbol
     */
    private formatAmount(amount: string, label?: string): any {
        // Extract the numeric value and SAR text
        const amountParts = amount.split(' ');
        const numericValue = amountParts[0];

        // Return formatted amount with SVG symbol
        return (
            <div class="installments-amount">
                <div class="amount-container">
                    {label && <span class="installments-label">{label}</span>}
                    <span class="installments-amount-value">{numericValue}</span>
                </div>
            </div>
        );
    }

    /**
     * Render an installment step
     */
    private renderInstallmentStep(
        amount: string,
        label: string,
        isFirst: boolean = false,
        isLast: boolean = false,
        progressType: 'empty' | 'small' | 'medium' | 'full' = 'empty'
    ) {
        return (
            <div class="installments-step">
                {isFirst
                    ? this.formatAmount(amount, this.t('Hold'))
                    : this.formatAmount(amount)
                }
                <div class="installments-circle-container">
                    {isFirst ? (
                        <div class="installments-circle-filled-first"></div>
                    ) : isLast || progressType === 'full' ? (
                        <div class="installments-circle-filled"></div>
                    ) : (
                        [
                            <div class="installments-circle-outline"></div>,
                            progressType === 'small' && (
                                <div class="installments-circle-segment installments-circle-segment-small"></div>
                            ),
                            progressType === 'medium' && (
                                <div class="installments-circle-segment installments-circle-segment-medium"></div>
                            )
                        ]
                    )}
                </div>
                <div class="installments-label">{label}</div>
            </div>
        );
    }

    /**
     * Render a connector between steps
     */
    private renderConnector(isDotted: boolean = false) {
        return (
            <div class="installments-connector-container">
                {isDotted ? (
                    <div class="installments-dotted-connector">
                        <div class="installments-connector-dot"></div>
                        <div class="installments-dots-container">
                            <div class="installments-dot"></div>
                            <div class="installments-dot"></div>
                            <div class="installments-dot"></div>
                            <div class="installments-dot"></div>
                            <div class="installments-dot"></div>
                        </div>
                        <div class="installments-connector-dot"></div>
                    </div>
                ) : (
                    <div class="installments-simple-connector">
                        <div class="installments-connector-dot"></div>
                        <div class="installments-connector-line"></div>
                        <div class="installments-connector-dot"></div>
                    </div>
                )}
            </div>
        );
    }

    render() {
        // If installments are not enabled, don't render anything
        if (!this.enableInstallments) {
            return null;
        }
        if (this.debug) {
            console.log('[AmwalInstallmentsTimeline] Debug Mode Enabled');
            console.log('[AmwalInstallmentsTimeline] Installments:', this.installments);
            console.log('[AmwalInstallmentsTimeline] Installments Count:', this.installmentsCount);
            console.log('[AmwalInstallmentsTimeline] Amount:', this.amount);
            console.log('[AmwalInstallmentsTimeline] Max Visible Installments:', this.maxVisibleInstallments);
            console.log('[AmwalInstallmentsTimeline] Locale:', this.locale);
            console.log('[AmwalInstallmentsTimeline] Enable Border:', this.border);
        }

        const allInstallments = this.installments;
        const installmentsToDisplay = [];

        // Handle visibility logic for many installments
        if (allInstallments.length <= this.maxVisibleInstallments) {
            // If we have fewer installments than max, show all
            installmentsToDisplay.push(...allInstallments);
        } else {
            // If we have more, show first, then ellipsis, then last
            installmentsToDisplay.push(
                allInstallments[0],
                allInstallments[1]
            );
            installmentsToDisplay.push(
                allInstallments[allInstallments.length - 1]
            );
        }

        return (
            <div id="paymentProgress"
                 style={this.locale === 'ar' ? { direction: 'rtl' } : {}}
                 class={{
                     'installments': true,
                     'payment-progress': true,
                     'bordered': this.border
                 }}>
                {/* Today / Initial Payment */}
                {this.renderInstallmentStep(
                    `${this.amount}`,
                    this.t('Today'),
                    true,
                    false,
                    'empty'
                )}

                {/* Connector between Initial Payment and First Installment */}
                {this.renderConnector()}

                {/* Display the installments */}
                {installmentsToDisplay.map((installment, index) => [
                    this.renderInstallmentStep(
                        installment.amount,
                        this.t('Installment') + ' ' + (index === 2 && allInstallments.length > 3 ? allInstallments.length : index + 1),
                        false,
                        index === installmentsToDisplay.length - 1,
                        installment.progressType
                    ),
                    index < installmentsToDisplay.length - 1 &&
                    this.renderConnector(index === 1 && allInstallments.length > 3)
                ])}
            </div>
        );
    }
}